/**
 * list_organizations Tool - Discover organizations the user belongs to
 *
 * Essential for AI agents to navigate the workspace and find projects.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';

export const listOrganizationsToolDefinition: Tool = {
  name: 'list_organizations',
  description: `List all organizations the authenticated user belongs to.

Organizations contain projects and manage billing. Use this tool to discover available organizations before creating jobs.

Returns organization name, ID, project count, and member count.

Typical workflow:
1. list_organizations → Find the right organization
2. list_projects(organizationId: "...") → Find the right project
3. create_job(projectId: "...") → Create a test job`,
  inputSchema: {
    type: 'object',
    properties: {},
    required: [],
  },
};

export async function handleListOrganizations(
  jobClient: JobClient
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    const data = await jobClient.listOrganizations();

    if (data.items.length === 0) {
      return {
        content: [{
          type: 'text',
          text: `No organizations found.

You need to be a member of at least one organization to create test jobs.
Visit https://runhuman.com/dashboard to create or join an organization.`,
        }],
      };
    }

    const orgList = data.items.map(org => {
      let entry = `- ${org.name} (ID: ${org.id})`;
      if (org.projectCount !== undefined) entry += `\n  Projects: ${org.projectCount}`;
      if (org.memberCount !== undefined) entry += `\n  Members: ${org.memberCount}`;
      return entry;
    }).join('\n\n');

    return {
      content: [{
        type: 'text',
        text: `Found ${data.items.length} organization(s):

${orgList}

Next steps:
- List projects: list_projects(organizationId: "<org-id>")
- Create a job: create_job(projectId: "<project-id>")`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };

    return {
      content: [{
        type: 'text',
        text: `Failed to list organizations

Error: ${err.message}
${err.status ? `Status: ${err.status}` : ''}

Please check your PAT is valid (create one at https://runhuman.com/dashboard/settings/tokens).`,
      }],
      isError: true,
    };
  }
}
